import {
  EAnalyticsEvent,
  toAnalyticsErrorName,
} from "@frontend-shared/product-analytics";
import { Component, type ErrorInfo, type ReactNode } from "react";

import { productAnalytics } from "@/lib/product-analytics";

import { RouteErrorFallback } from "./RouteErrorFallback";

interface IRouteErrorBoundaryProps {
  children: ReactNode;
}

interface IRouteErrorBoundaryState {
  hasError: boolean;
}

// NOTE: A stale bundle after a deploy is by far the most common way a route
// white-screens, and it is not a bug — the user just needs a reload. Separating
// it keeps a deploy from reading as a spike of real crashes. Matched on the
// message because browsers disagree on the shape: Chrome throws
// `ChunkLoadError`, Firefox and Safari throw a plain `TypeError`. Only the
// verdict leaves the app; the text this reads is never sent.
const CHUNK_LOAD_ERROR_PATTERN =
  /loading chunk|loading css chunk|dynamically imported module|importing a module script failed/i;

const getIsChunkLoadError = (error: Error): boolean =>
  error.name === "ChunkLoadError" ||
  CHUNK_LOAD_ERROR_PATTERN.test(error.message);

// NOTE: Lazy route chunks and page render logic can both throw — a failed
// dynamic import rejects past <Suspense> (which only catches pending, not
// rejected, promises) and a render-time crash (e.g. parsing an unexpected API
// shape) bubbles up the tree. With no boundary above the layout, either one
// white-screens the whole app. Catching at the route level keeps the
// surrounding shell (header, sidebar) mounted and shows a recoverable fallback
// in the content area instead.
export class RouteErrorBoundary extends Component<
  IRouteErrorBoundaryProps,
  IRouteErrorBoundaryState
> {
  state: IRouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): IRouteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // NOTE: NSAI-2434 — a white-screened route is the one failure a user never
    // reports; they just leave. This is the only place it becomes observable.
    //
    // Uses the module singleton rather than `useProductAnalytics` because an
    // error boundary must be a class component — hooks cannot be called here.
    //
    // Sends the error's *class* and route, never `error.message` and never the
    // component stack. Message text reads as developer-authored but is not:
    // `JSON.parse` quotes the offending payload back, fetch layers interpolate
    // URLs (signed ones included), and libraries splice in filenames and
    // user-entered values. Forwarding it verbatim would be an open path for
    // customer content to reach PostHog, which the tracking plan forbids
    // outright. NSAI-2433 — `error.name` is writable too, so it goes through
    // the shared allowlist (`Other` for anything unrecognised); the chunk-load
    // flag below recovers the one distinction the message was being read for.
    productAnalytics.capture({
      event: EAnalyticsEvent.UI_ERROR_BOUNDARY_TRIGGERED,
      properties: {
        error_name: toAnalyticsErrorName({ error }),
        is_chunk_load_error: getIsChunkLoadError(error),
        pathname: globalThis.location.pathname,
      },
    });

    console.warn(
      "[RouteErrorBoundary] Route render error:",
      error.message,
      info,
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <RouteErrorFallback />;
    }
    return this.props.children;
  }
}
